import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { COOKIE_NAMES, ThemeSettings, getCookie, routes } from '@fly/shared';
import { useSystemTheme, useUser } from '@fly/shared-hooks';

import { type SubMenuListItems } from './SubMenuList';

const SUPPORT_EMAIL = import.meta.env.VITE_SUPPORT_EMAIL;

const getEmailBody = (email: string, accountName: string) =>
  `# DO NOT MODIFY THIS SECTION\nUser: ${email}\nAccount: ${accountName}\n# DO NOT MODIFY THIS SECTION\n\nHi Fly Team,\n\n[Please describe your issue or request here]`;
const getGmailUrl = (email: string, accountName: string) =>
  `https://mail.google.com/mail/?${new URLSearchParams({
    view: 'cm',
    to: SUPPORT_EMAIL,
    su: 'JFrog Fly Support Request - Do Not Modify',
    body: getEmailBody(email, accountName),
  }).toString()}`;
const getNativeAppUrl = (email: string, accountName: string) =>
  `mailto:${SUPPORT_EMAIL}?subject=JFrog Fly Support Request - Do Not Modify&body=${encodeURIComponent(getEmailBody(email, accountName))}`;

export function useAccountMenuItems(): SubMenuListItems {
  const { t } = useTranslation();
  const { email } = useUser();
  const { setTheme, themeSetting } = useSystemTheme();

  return useMemo((): SubMenuListItems => {
    return [
      {
        key: 'account',
        items: [
          {
            title: t('app.dashboard.header.user_menu.account.links.profile', {
              defaultValue: 'My Profile',
            }),
            rootButtonProps: {
              to: routes.DASHBOARD.ACCOUNT.PROFILE,
              iconStart: 'user',
            },
          },
          {
            title: t('app.dashboard.header.user_menu.account.links.theme', {
              defaultValue: 'Theme',
            }),
            rootButtonProps: {
              iconStart: 'half_2',
            },
            subItems: [
              {
                key: 'theme_light',
                items: [
                  {
                    title: t(
                      'app.dashboard.header.user_menu.account.links.theme_dark',
                      {
                        defaultValue: 'Dark',
                      }
                    ),
                    rootButtonProps: {
                      iconStart:
                        themeSetting === ThemeSettings.DARK ? 'check' : '_',
                      className: 'w-full justify-start',
                      onClick: () => setTheme(ThemeSettings.DARK),
                    },
                  },
                  {
                    title: t(
                      'app.dashboard.header.user_menu.account.links.theme_light',
                      {
                        defaultValue: 'Light',
                      }
                    ),
                    rootButtonProps: {
                      iconStart:
                        themeSetting === ThemeSettings.LIGHT ? 'check' : '_',
                      className: 'w-full text-left justify-start',
                      onClick: () => setTheme(ThemeSettings.LIGHT),
                    },
                  },
                  {
                    title: t(
                      'app.dashboard.header.user_menu.account.links.theme_system',
                      {
                        defaultValue: 'System theme',
                      }
                    ),
                    rootButtonProps: {
                      iconStart:
                        themeSetting === ThemeSettings.SYSTEM ? 'check' : '_',
                      className: 'w-full text-left justify-start',
                      onClick: () => setTheme(ThemeSettings.SYSTEM),
                    },
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        key: 'workspace',
        items: [
          {
            title: t(
              'app.dashboard.header.user_menu.workspace.links.team_management',
              {
                defaultValue: 'Team Management',
              }
            ),
            rootButtonProps: {
              to: routes.DASHBOARD.ACCOUNT.TEAM_MANAGEMENT,
              iconStart: 'invite_user',
            },
          },
          {
            title: t(
              'app.dashboard.header.user_menu.workspace.links.token_management',
              {
                defaultValue: 'Token Management',
              }
            ),
            rootButtonProps: {
              to: routes.DASHBOARD.ACCOUNT.TOKEN_MANAGEMENT,
              iconStart: 'key',
            },
            button: {
              title: t(
                'app.dashboard.header.user_menu.workspace.buttons.generate_token',
                {
                  defaultValue: 'Generate Token',
                }
              ),
              to: routes.DASHBOARD.ACCOUNT.TOKEN_GENERATE,
            },
          },
        ],
      },
      {
        key: 'settings',
        items: [
          {
            title: (
              <span className='w-full text-left'>
                {t('app.dashboard.header.user_menu.settings.links.contact_us', {
                  defaultValue: 'Contact Us',
                })}
              </span>
            ),
            rootButtonProps: {
              iconStart: 'mail',
            },
            subItems: [
              {
                key: 'mail',
                items: [
                  {
                    title: t(
                      'app.dashboard.header.user_menu.settings.links.email_via_gmail',
                      {
                        defaultValue: 'Email via Gmail',
                      }
                    ),
                    rootButtonProps: {
                      className: 'w-full justify-start',
                      href: getGmailUrl(
                        email || '',
                        getCookie(COOKIE_NAMES.TEAM_NAME) || ''
                      ),
                      target: '_blank',
                    },
                  },
                  {
                    title: t(
                      'app.dashboard.header.user_menu.settings.links.email_via_native_app',
                      {
                        defaultValue: 'Email via Native App',
                      }
                    ),
                    rootButtonProps: {
                      className: 'w-full justify-start',
                      href: getNativeAppUrl(
                        email || '',
                        getCookie(COOKIE_NAMES.TEAM_NAME) || ''
                      ),
                      target: '_blank',
                    },
                  },
                ],
              },
            ],
          },
          {
            title: t('app.dashboard.header.user_menu.settings.docs', {
              defaultValue: 'Fly Docs',
            }),
            rootButtonProps: {
              iconStart: 'question_circle',
              href: 'https://docs.fly.jfrog.com/',
              target: '_blank',
            },
          },
        ],
      },
    ];
  }, [t, email, setTheme, themeSetting]);
}
